import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, Trash2, ShoppingBag, ShieldCheck, Truck, Snowflake, Award, ChevronRight } from 'lucide-react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CTA } from '../components/CTA';
import { Vial } from '../components/Vial';
import { ProductCard } from '../components/ProductCard';
import { PRODUCTS } from '../components/products';
import { useCart, BULK_TIERS, getDiscountForQty } from '../components/cart/CartContext';
export function Cart() {
  const {
    lines,
    setQty,
    removeItem,
    subtotal,
    totalSavings,
    totalCount
  } = useCart();
  const hydrated = lines.map(l => {
    const p = PRODUCTS.find(x => x.id === l.productId);
    return p ? {
      ...l,
      product: p
    } : null;
  }).filter(Boolean) as Array<{
    productId: string;
    qty: number;
    product: (typeof PRODUCTS)[number];
  }>;
  // Upsell suggestions — peptides not in cart, prioritize same-goal items
  const cartGoals = new Set(hydrated.flatMap(h => h.product.goals));
  const upsells = PRODUCTS.filter(p => !lines.find(l => l.productId === p.id)).sort((a, b) => {
    const aMatch = a.goals.some(g => cartGoals.has(g)) ? 1 : 0;
    const bMatch = b.goals.some(g => cartGoals.has(g)) ? 1 : 0;
    return bMatch - aMatch;
  }).slice(0, 3);
  const nextTier = (() => {
    if (totalCount === 0) return null;
    const currentDiscount = getDiscountForQty(totalCount);
    return BULK_TIERS.find(t => t.discount > currentDiscount) ?? null;
  })();
  const shippingThreshold = 75;
  const freeShipping = subtotal >= shippingThreshold;
  return <div className="min-h-screen w-full bg-background" data-id="element-1144">
      <AnnouncementBar data-id="element-1145" />
      <Header data-id="element-1146" />
      <main data-id="element-1147">
        {/* Breadcrumb */}
        <div className="w-full border-b border-border" data-id="element-1148">
          <div className="container mx-auto py-5" data-id="element-1149">
            <nav className="flex items-center gap-1.5 text-[12px] text-muted-foreground" aria-label="Breadcrumb" data-id="element-1150">
              <Link to="/" className="hover:text-foreground transition-colors" data-id="element-1151">
                Home
              </Link>
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-1152" />
              <span className="text-foreground" data-id="element-1153">Cart</span>
            </nav>
          </div>
        </div>

        <section className="container mx-auto pt-10 lg:pt-14 pb-16 lg:pb-24" data-id="element-1154">
          <div className="mb-10 lg:mb-12 flex items-end justify-between gap-6" data-id="element-1155">
            <div data-id="element-1156">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-3" data-id="element-1157">
                Cart
              </p>
              <h1 className="text-3xl lg:text-[44px] font-semibold tracking-[-0.025em] text-foreground leading-[1.05]" data-id="element-1158">
                {totalCount === 0 ? 'Your cart is empty.' : `${totalCount} ${totalCount === 1 ? 'vial' : 'vials'} ready to ship.`}
              </h1>
            </div>
          </div>

          {hydrated.length === 0 ? <EmptyState data-id="element-1159" /> : <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-14 items-start" data-id="element-1160">
              {/* Lines + bulk savings */}
              <div data-id="element-1161">
                {/* Bulk pricing reminder */}
                <BulkPricingHint totalCount={totalCount} nextTier={nextTier} data-id="element-1162" />

                <div className="rounded-2xl border border-border bg-card overflow-hidden" data-id="element-1163">
                  <ul className="divide-y divide-border" data-id="element-1164">
                    {hydrated.map(({
                  product,
                  qty
                }) => {
                  const d = getDiscountForQty(qty);
                  const gross = product.price * qty;
                  const net = gross * (1 - d);
                  return <li key={product.id} className="p-5 flex gap-5" data-id="element-1165">
                          <Link to={`/shop/${product.slug}`} className="h-32 w-24 rounded-xl bg-secondary/50 border border-border flex items-center justify-center flex-shrink-0 text-foreground" data-id="element-1166">
                            <Vial label="" doseMg={product.doseMg} volumeMl={product.volumeMl} category={product.category} size={56} showShadow={false} showHighlight={false} data-id="element-1167" />
                          </Link>
                          <div className="flex-1 min-w-0 flex flex-col" data-id="element-1168">
                            <div className="flex items-start justify-between gap-4" data-id="element-1169">
                              <div className="min-w-0" data-id="element-1170">
                                <Link to={`/shop/${product.slug}`} className="text-[16px] font-semibold tracking-tight text-foreground hover:text-primary transition-colors" data-id="element-1171">
                                  {product.name}
                                </Link>
                                <p className="text-[13px] text-muted-foreground mt-0.5 line-clamp-1" data-id="element-1172">
                                  {product.tagline}
                                </p>
                                <div className="font-mono text-[11px] text-muted-foreground mt-2" data-id="element-1173">
                                  <span className="text-foreground" data-id="element-1174">
                                    {product.doseMg}
                                  </span>
                                  <span className="text-primary font-semibold" data-id="element-1175">
                                    mg
                                  </span>
                                  {' · '}
                                  {product.purity.toFixed(1)}% · {product.batch}
                                </div>
                              </div>
                              <div className="text-right whitespace-nowrap" data-id="element-1176">
                                {d > 0 && <div className="font-mono text-[11px] text-muted-foreground line-through leading-none" data-id="element-1177">
                                    £{gross.toFixed(0)}
                                  </div>}
                                <div className={`font-mono text-[18px] font-medium leading-tight mt-0.5 ${d > 0 ? 'text-primary' : 'text-foreground'}`} data-id="element-1178">
                                  £{net.toFixed(0)}
                                </div>
                                {d > 0 && <div className="text-[10px] uppercase tracking-wider text-primary font-semibold mt-0.5" data-id="element-1179">
                                    {Math.round(d * 100)}% off
                                  </div>}
                              </div>
                            </div>

                            <div className="mt-auto pt-4 flex items-center justify-between gap-3" data-id="element-1180">
                              <div className="inline-flex items-center rounded-full border border-border bg-background overflow-hidden" data-id="element-1181">
                                <button onClick={() => setQty(product.id, qty - 1)} className="h-9 w-10 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Decrease" data-id="element-1182">
                                  <Minus className="h-3 w-3" strokeWidth={1.75} data-id="element-1183" />
                                </button>
                                <span className="font-mono text-[14px] w-8 text-center text-foreground select-none" data-id="element-1184">
                                  {qty}
                                </span>
                                <button onClick={() => setQty(product.id, qty + 1)} className="h-9 w-10 inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors" aria-label="Increase" data-id="element-1185">
                                  <Plus className="h-3 w-3" strokeWidth={1.75} data-id="element-1186" />
                                </button>
                              </div>
                              <button onClick={() => removeItem(product.id)} className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors" data-id="element-1187">
                                <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} data-id="element-1188" />
                                Remove
                              </button>
                            </div>
                          </div>
                        </li>;
                })}
                  </ul>
                </div>

                {/* Upsells */}
                {upsells.length > 0 && <div className="mt-12" data-id="element-1189">
                    <div className="flex items-end justify-between mb-6" data-id="element-1190">
                      <div data-id="element-1191">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium mb-2" data-id="element-1192">
                          Frequently added
                        </p>
                        <h2 className="text-xl font-semibold tracking-tight text-foreground" data-id="element-1193">
                          Researchers also buy these.
                        </h2>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1194">
                      {upsells.map(p => <ProductCard key={p.id} product={p} data-id="element-1195" />)}
                    </div>
                  </div>}
              </div>

              {/* Summary */}
              <motion.aside initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="lg:sticky lg:top-24" data-id="element-1196">
                <div className="rounded-2xl border border-border bg-card p-6" data-id="element-1197">
                  <h3 className="text-[15px] font-semibold tracking-tight text-foreground mb-5" data-id="element-1198">
                    Order summary
                  </h3>

                  <div className="space-y-2.5 text-[13px]" data-id="element-1199">
                    <div className="flex justify-between text-muted-foreground" data-id="element-1200">
                      <span data-id="element-1201">
                        Subtotal ({totalCount}{' '}
                        {totalCount === 1 ? 'item' : 'items'})
                      </span>
                      <span className="font-mono text-foreground" data-id="element-1202">
                        £{(subtotal + totalSavings).toFixed(0)}
                      </span>
                    </div>
                    {totalSavings > 0 && <div className="flex justify-between" data-id="element-1203">
                        <span className="text-primary font-medium" data-id="element-1204">
                          Bulk savings
                        </span>
                        <span className="font-mono text-primary font-medium" data-id="element-1205">
                          −£{totalSavings.toFixed(0)}
                        </span>
                      </div>}
                    <div className="flex justify-between text-muted-foreground" data-id="element-1206">
                      <span data-id="element-1207">Shipping</span>
                      <span className={`font-mono ${freeShipping ? 'text-primary font-medium' : 'text-foreground'}`} data-id="element-1208">
                        {freeShipping ? 'Free' : '£4.95'}
                      </span>
                    </div>
                  </div>

                  {!freeShipping && <div className="mt-4 px-3 py-2.5 rounded-lg bg-primary/5 border border-primary/15" data-id="element-1209">
                      <p className="text-[11.5px] text-foreground leading-relaxed" data-id="element-1210">
                        Add{' '}
                        <span className="font-mono font-semibold" data-id="element-1211">
                          £{(shippingThreshold - subtotal).toFixed(0)}
                        </span>{' '}
                        more for{' '}
                        <span className="text-primary font-semibold" data-id="element-1212">
                          free UK delivery
                        </span>
                        .
                      </p>
                      <div className="mt-2 h-1 w-full rounded-full bg-border overflow-hidden" data-id="element-1213">
                        <div className="h-full bg-primary rounded-full transition-all duration-500" style={{
                    width: `${Math.min(100, subtotal / shippingThreshold * 100)}%`
                  }} data-id="element-1214" />
                      </div>
                    </div>}

                  <div className="my-5 border-t border-border" data-id="element-1215" />

                  <div className="flex items-baseline justify-between mb-5" data-id="element-1216">
                    <span className="text-[14px] font-semibold text-foreground" data-id="element-1217">
                      Total
                    </span>
                    <span className="font-mono text-[26px] font-medium text-foreground tracking-[-0.015em]" data-id="element-1218">
                      £{(subtotal + (freeShipping ? 0 : 4.95)).toFixed(2)}
                    </span>
                  </div>

                  <CTA variant="accent" size="lg" fullWidth data-id="element-1219">
                    Continue to checkout
                  </CTA>
                  <p className="mt-3 text-[10.5px] text-center text-muted-foreground" data-id="element-1220">
                    Taxes calculated at checkout. Secure payment.
                  </p>
                </div>

                {/* Trust */}
                <div className="mt-5 rounded-2xl border border-border bg-card p-5" data-id="element-1221">
                  <ul className="space-y-3 text-[12.5px]" data-id="element-1222">
                    {[{
                  icon: Award,
                  label: 'ISO 17025 third-party tested'
                }, {
                  icon: Truck,
                  label: 'Same-day UK dispatch before 2pm'
                }, {
                  icon: Snowflake,
                  label: 'Cold-chain insulated packaging'
                }, {
                  icon: ShieldCheck,
                  label: '30-day money-back guarantee'
                }].map(({
                  icon: Icon,
                  label
                }) => <li key={label} className="flex items-center gap-3 text-foreground" data-id="element-1223">
                        <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-1224">
                          <Icon className="h-3.5 w-3.5 text-primary" strokeWidth={1.75} data-id="element-1225" />
                        </div>
                        {label}
                      </li>)}
                  </ul>
                </div>
              </motion.aside>
            </div>}
        </section>
      </main>
      <Footer data-id="element-1226" />
    </div>;
}
function BulkPricingHint({
  totalCount,
  nextTier
}: {
  totalCount: number;
  nextTier: (typeof BULK_TIERS)[number] | null;
}) {
  if (!nextTier) {
    return <div className="mb-6 rounded-2xl border border-primary/30 bg-primary/5 p-5 flex items-center gap-4" data-id="element-1227">
        <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0" data-id="element-1228">
          <Award className="h-4 w-4" strokeWidth={2} data-id="element-1229" />
        </div>
        <div data-id="element-1230">
          <div className="text-[14px] font-semibold text-foreground" data-id="element-1231">
            Maximum savings unlocked
          </div>
          <div className="text-[12.5px] text-muted-foreground mt-0.5" data-id="element-1232">
            You're getting the highest available bulk discount on this order.
          </div>
        </div>
      </div>;
  }
  const remaining = nextTier.qty - totalCount;
  return <div className="mb-6 rounded-2xl border border-border bg-card p-5" data-id="element-1233">
      <div className="flex items-center justify-between gap-4" data-id="element-1234">
        <div data-id="element-1235">
          <div className="text-[13px] font-semibold text-foreground" data-id="element-1236">
            Add <span className="font-mono text-primary" data-id="element-1237">{remaining}</span> more{' '}
            {remaining === 1 ? 'vial' : 'vials'} to{' '}
            <span className="text-primary" data-id="element-1238">
              {nextTier.savingsLabel?.toLowerCase()}
            </span>
          </div>
          <div className="text-[11.5px] text-muted-foreground mt-1" data-id="element-1239">
            Bulk pricing applies across any combination of peptides in your
            cart.
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold whitespace-nowrap" data-id="element-1240">
          Next: {nextTier.label}
        </span>
      </div>
      <div className="mt-3 flex gap-1" data-id="element-1241">
        {BULK_TIERS.map(t => <div key={t.qty} className="flex-1 h-1.5 rounded-full overflow-hidden bg-border" data-id="element-1242">
            <div className="h-full bg-primary transition-all duration-500" style={{
          width: totalCount >= t.qty ? '100%' : '0%'
        }} data-id="element-1243" />
          </div>)}
      </div>
      <div className="mt-2 flex justify-between text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-id="element-1244">
        {BULK_TIERS.map(t => <span key={t.qty} className={totalCount >= t.qty ? 'text-primary font-semibold' : ''} data-id="element-1245">
            {t.qty}+
          </span>)}
      </div>
    </div>;
}
function EmptyState() {
  return <div className="rounded-2xl border border-border bg-card py-20 px-6 flex flex-col items-center text-center" data-id="element-1246">
      <div className="h-14 w-14 rounded-full bg-secondary border border-border flex items-center justify-center mb-5" data-id="element-1247">
        <ShoppingBag className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} data-id="element-1248" />
      </div>
      <h2 className="text-xl font-semibold tracking-tight text-foreground" data-id="element-1249">
        Your cart is empty
      </h2>
      <p className="text-[14px] text-muted-foreground mt-2 max-w-md leading-relaxed" data-id="element-1250">
        Browse our range of independently HPLC-verified research peptides — all
        shipped same-day from the UK.
      </p>
      <div className="mt-7" data-id="element-1251">
        <CTA to="/shop" variant="primary" size="lg" data-id="element-1252">
          Shop peptides
        </CTA>
      </div>
    </div>;
}